import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);
    } catch (error) {
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-[#86868B]">Loading...</div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-semibold text-[#1D1D1F] font-['Outfit'] mb-4">
            Article Not Found
          </h1>
          <p className="text-[#86868B] mb-8">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-[#007AFF] font-medium hover:gap-3 transition-all"
          >
            <ArrowLeft size={18} />
            Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="article-page" className="bg-white">
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={article.image_url}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 -mt-32 relative z-10">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl shadow-xl p-8 md:p-12"
        >
          <Link
            to="/insights"
            data-testid="back-to-insights"
            className="inline-flex items-center gap-2 text-[#007AFF] font-medium hover:gap-3 transition-all mb-8"
          >
            <ArrowLeft size={18} />
            Back to Insights
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1 bg-[#F5F5F7] text-[#86868B] text-sm font-medium rounded-full capitalize">
              <Tag size={14} />
              {article.category}
            </span>
            <span className="inline-flex items-center gap-2 text-[#86868B] text-sm">
              <Calendar size={14} />
              {formatDate(article.created_at)}
            </span>
          </div>

          <h1 
            data-testid="article-title"
            className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] font-['Outfit'] mb-6 leading-tight"
          >
            {article.title}
          </h1>

          <p className="text-lg text-[#86868B] mb-8 leading-relaxed">
            {article.description}
          </p>

          <div 
            data-testid="article-content"
            className="prose prose-lg max-w-none text-[#1D1D1F]"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {article.content}
          </div>
        </motion.article>
      </div>

      {/* Spacing */}
      <div className="h-24" />
    </div>
  );
};

export default ArticlePage;
